import type { IdentityApi } from '@backstage/core-plugin-api';
import type { ApplicationSummary, Department } from './types';

// The Platform Controller API this app talks to — see
// platform-controller/src/applications/. Routed through Backstage's own
// backend proxy (app-config.yaml's proxy.endpoints) rather than the browser
// fetching platform-controller directly: a deployed HTTPS page fetching a
// plain-HTTP target hit browser mixed-content blocking (real bug found live
// — curl succeeded, the browser's fetch() failed with "Failed to fetch").
// A relative path resolves against the page's own origin, so this is a
// same-origin, same-scheme request in both dev and deployed mode.
export const PLATFORM_CONTROLLER_URL = '/api/proxy/platform-controller';

// Backstage's own `fetchApiRef` does NOT auto-attach the signed-in user's
// token to an arbitrary custom path like this one — it only does so for
// requests it recognizes via discovery (known plugin base URLs). Without an
// explicit Authorization header, /api/proxy/* rejects the request with a
// real 401 "Missing credentials" (found live: curl with a manually-fetched
// token succeeded, the browser's fetchApi-driven request 401'd). Fetch the
// credentials from IdentityApi and attach the header ourselves instead.
async function authHeaders(identityApi: IdentityApi): Promise<HeadersInit> {
  const { token } = await identityApi.getCredentials();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Header platform-controller reads the acting user's email from, for the
 * delete allowlist. Must match ACTOR_EMAIL_HEADER in
 * platform-controller/src/applications/applications.controller.ts. */
const ACTOR_EMAIL_HEADER = 'X-IDP-Actor-Email';

async function actorHeaders(identityApi: IdentityApi, actorEmail: string | undefined): Promise<HeadersInit> {
  return { ...(await authHeaders(identityApi)), ...(actorEmail ? { [ACTOR_EMAIL_HEADER]: actorEmail } : {}) };
}

export interface CreateApplicationPayload {
  rawManifest: unknown;
  actorRole: 'DEVELOPER';
  template: string;
  deploy: true;
  extraEnvVars?: Record<string, string>;
}

export async function listApplications(identityApi: IdentityApi): Promise<ApplicationSummary[]> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications -> ${res.status}`);
  }
  return res.json();
}

export async function createAndDeployApplication(
  identityApi: IdentityApi,
  payload: CreateApplicationPayload,
): Promise<{ workflowId: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(identityApi)) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body === 'object' ? JSON.stringify(body) : String(body));
  }
  return res.json();
}

export interface CreateFullStackApplicationPayload {
  backendRawManifest: unknown;
  frontendRawManifest: unknown;
  actorRole: 'DEVELOPER';
  extraEnvVars?: Record<string, string>;
}

export async function createAndDeployFullStackApplication(
  identityApi: IdentityApi,
  payload: CreateFullStackApplicationPayload,
): Promise<{ workflowId: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/fullstack`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(identityApi)) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body === 'object' ? JSON.stringify(body) : String(body));
  }
  return res.json();
}

export interface DeployExistingRepositoryPayload {
  rawManifest: unknown;
  actorRole: 'DEVELOPER';
  /** e.g. "mumzworld-tech/some-existing-repo". Cloned read-only, never
   * created or pushed to. */
  repoFullName: string;
  /** Branch or tag; defaults to the repo's default branch. */
  ref?: string;
  extraEnvVars?: Record<string, string>;
}

/**
 * Deploys an already-existing GitHub repository instead of scaffolding a new
 * one from a golden template. Returns a Temporal workflowId — poll
 * `getWorkflowStatus` for the outcome, same as createAndDeployApplication.
 */
export async function deployExistingRepository(
  identityApi: IdentityApi,
  payload: DeployExistingRepositoryPayload,
): Promise<{ workflowId: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/deploy-existing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(identityApi)) },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body === 'object' ? JSON.stringify(body) : String(body));
  }
  return res.json();
}

export interface RepoInspection {
  detectedApplicationType?: string;
  detectedRuntime?: string;
  source: 'platform.yaml' | 'heuristic' | 'none';
}

/**
 * Read-only precheck for "Deploy Existing Repo" — asks what the repo
 * actually is (via its platform.yaml, or a file-presence heuristic) before
 * the user submits. See platform-controller/src/applications/repo-inspection.ts.
 * Real incident this exists for: an ai-agent-python repo deployed through
 * the "Frontend App" kind came up exited/unhealthy because the port/
 * healthcheck the kind assumed didn't match what the repo actually served.
 */
export async function inspectExistingRepository(
  identityApi: IdentityApi,
  repoFullName: string,
  ref?: string,
): Promise<RepoInspection> {
  const params = new URLSearchParams({ repoFullName, ...(ref ? { ref } : {}) });
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/inspect-repo?${params.toString()}`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/inspect-repo -> ${res.status}`);
  }
  return res.json();
}

export interface NameAvailability {
  available: boolean;
  repoName: string;
}

/** Live check against GitHub (no database — see the design note in
 * platform-controller/src/applications/github-availability.ts) so the form
 * can tell a user their name is taken before they submit. `department` (a
 * slug) is folded into the checked repo name — see
 * repoNameForApplication in github-availability.ts — so the check matches
 * what will actually be created. */
export async function checkAppNameAvailability(
  identityApi: IdentityApi,
  name: string,
  department?: string,
): Promise<NameAvailability> {
  const params = new URLSearchParams({ name, ...(department ? { department } : {}) });
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/check-name?${params.toString()}`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/check-name -> ${res.status}`);
  }
  return res.json();
}

/**
 * The platform's department taxonomy (see platform-controller/src/departments/)
 * — not a database, a small GitHub-backed list. Used to populate the
 * "Department" select in every create flow and to prefix a newly-created
 * repo's name (`sre-idp-<department>-<app>`).
 */
export async function listDepartments(identityApi: IdentityApi): Promise<Department[]> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/departments`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /departments -> ${res.status}`);
  }
  return res.json();
}

/** Adds a new department to the taxonomy — `slug` is optional and
 * auto-derived from `name` server-side when omitted. */
export async function createDepartment(
  identityApi: IdentityApi,
  name: string,
  slug?: string,
): Promise<Department> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/departments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders(identityApi)) },
    body: JSON.stringify({ name, ...(slug ? { slug } : {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body === 'object' ? JSON.stringify(body) : String(body));
  }
  return res.json();
}

export interface WorkflowStatusResponse {
  status: string;
  result?: Record<string, unknown>;
}

export async function getWorkflowStatus(identityApi: IdentityApi, workflowId: string): Promise<WorkflowStatusResponse> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${workflowId}`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/${workflowId} -> ${res.status}`);
  }
  return res.json();
}

/** Real env vars Coolify has for this app — custom values set at create
 * time plus, when AWS resources were requested, the real Floci bucket/
 * queue/table identifiers the deploy workflow tagged on. */
export async function getApplicationEnv(
  identityApi: IdentityApi,
  applicationId: string,
): Promise<Record<string, string>> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${applicationId}/env`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/${applicationId}/env -> ${res.status}`);
  }
  return res.json();
}

/** Real container log lines from Coolify, not mocked. */
export async function getApplicationLogs(
  identityApi: IdentityApi,
  applicationId: string,
  lines = 200,
): Promise<{ logs: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${applicationId}/logs?lines=${lines}`, {
    headers: await authHeaders(identityApi),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/${applicationId}/logs -> ${res.status}`);
  }
  return res.json();
}

export interface PlatformPermissions {
  canDelete: boolean;
  canEdit: boolean;
}

/**
 * Asks the server what this user is allowed to do, rather than the frontend
 * keeping its own copy of the delete allowlist. Single source of truth lives
 * in platform-controller/src/applications/delete-allowlist.ts, so the list can
 * change server-side with no frontend release and the two can never drift.
 */
export async function getPlatformPermissions(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
): Promise<PlatformPermissions> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/permissions`, {
    headers: await actorHeaders(identityApi, actorEmail),
  });
  if (!res.ok) {
    throw new Error(`GET /applications/permissions -> ${res.status}`);
  }
  return res.json();
}

/**
 * Deletes the app's Coolify runtime. The generated `sre-idp-*` GitHub repo is
 * NOT touched, so the app can be redeployed from its still-intact source.
 *
 * Returns a Temporal workflowId — deletion is a workflow like every other
 * mutation here, so poll `getWorkflowStatus` to know when it finished rather
 * than assuming success on a 2xx (a 2xx means "accepted", not "deleted").
 */
export async function deleteApplication(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
  applicationId: string,
  applicationName: string,
): Promise<{ workflowId: string }> {
  const res = await fetch(
    `${PLATFORM_CONTROLLER_URL}/applications/${applicationId}?name=${encodeURIComponent(applicationName)}`,
    { method: 'DELETE', headers: await actorHeaders(identityApi, actorEmail) },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as { message?: string });
    throw new Error(body?.message ?? `DELETE /applications/${applicationId} -> ${res.status}`);
  }
  return res.json();
}

export interface RedeployPayload {
  /** Variable names to delete before redeploying. */
  removeEnvKeys?: string[];
  /** Created if absent, updated if already set. Values may be credentials, so
   * they are sent and never read back — GET /env redacts known secrets. */
  envVars?: Record<string, string>;
  /** Rebuild the image from the app's own repo first, picking up committed
   * code. Without it the redeploy re-runs the image Coolify already has, which
   * only applies an environment change. */
  rebuildFromRepo?: boolean;
  /** Branch or tag; defaults to the repo's default branch. */
  ref?: string;
  /** Give the app its own private database (safe to repeat) and set DATABASE_URL. */
  addDatabase?: 'postgres' | 'mysql';
}

/**
 * "Update app": uploads a new version (zip / folder) of an existing app. The
 * server commits it to the app's repo, rebuilds and redeploys — the user never
 * touches git. Returns a workflowId to poll with `getWorkflowStatus`.
 */
export async function updateApplicationSource(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
  applicationId: string,
  applicationName: string,
  source: { type: 'zip'; zipBase64: string } | { type: 'files'; files: { path: string; contentBase64: string }[] },
  extras: Pick<RedeployPayload, 'envVars' | 'removeEnvKeys' | 'addDatabase'> = {},
): Promise<{ workflowId: string; commitSha: string; repoFullName: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${applicationId}/update-source`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await actorHeaders(identityApi, actorEmail)) },
    body: JSON.stringify({ name: applicationName, source, ...extras }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body as { message?: string | string[] }).message;
    throw new Error(Array.isArray(msg) ? msg.join('; ') : (msg ?? `HTTP ${res.status}`));
  }
  return body as { workflowId: string; commitSha: string; repoFullName: string };
}

/**
 * Redeploys an existing application. Returns a Temporal workflowId — poll
 * `getWorkflowStatus` for the outcome, since a 2xx means the workflow started,
 * not that the app is running the new version.
 *
 * The repository is derived server-side from the application name, so the UI
 * does not need to know this platform's `sre-idp-<slug>` convention.
 */
export async function redeployApplication(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
  applicationId: string,
  applicationName: string,
  payload: RedeployPayload,
): Promise<{ workflowId: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${applicationId}/redeploy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await actorHeaders(identityApi, actorEmail)) },
    body: JSON.stringify({ ...payload, name: applicationName }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as { message?: string });
    throw new Error(body?.message ?? `POST /applications/${applicationId}/redeploy -> ${res.status}`);
  }
  return res.json();
}

export interface ComposeDeployResult {
  id: string;
  url: string;
  service: string;
  port: number;
}

/** Deploys a pasted docker-compose file — synchronous, see
 * platform-controller/src/applications/compose-deploy.ts. */
export async function deployComposeApp(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
  payload: { name: string; composeYaml: string; environment: string; envVars?: Record<string, string> },
): Promise<ComposeDeployResult> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/compose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await actorHeaders(identityApi, actorEmail)) },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body as { message?: string | string[] }).message;
    throw new Error(Array.isArray(msg) ? msg.join('; ') : (msg ?? `HTTP ${res.status}`));
  }
  return body as ComposeDeployResult;
}

export type SmartDeploySource =
  | { type: 'github'; repo: string }
  | { type: 'zip'; zipBase64: string }
  | { type: 'files'; files: { path: string; contentBase64: string }[] };

/** "Put your app online": ZIP / folder / GitHub repo in, Temporal workflowId
 * out. The server detects the tech stack — the caller never says what it is. */
export async function deploySmartApp(
  identityApi: IdentityApi,
  actorEmail: string | undefined,
  payload: {
    name: string;
    department?: string;
    environment?: string;
    envVars?: Record<string, string>;
    /** Optional private database; the app gets DATABASE_URL. */
    database?: 'postgres' | 'mysql';
    source: SmartDeploySource;
  },
): Promise<{ workflowId: string; repoFullName: string }> {
  const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/smart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await actorHeaders(identityApi, actorEmail)) },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body as { message?: string | string[] }).message;
    throw new Error(Array.isArray(msg) ? msg.join('; ') : (msg ?? `HTTP ${res.status}`));
  }
  return body as { workflowId: string; repoFullName: string };
}
